/**
 * Creates a throttled async validator with caching.
 * The returned function will delay calls to the async function
 * until there has been a pause of `throttleMs` milliseconds.
 * @param asyncFn the async function to wrap
 * @param {{ throttleMs: number }} throttleMs value in milliseconds to throttle calls to the async function
 * @returns
 */
export function createAsyncValidator<TPayload, TResult>(
  asyncFn: (payload: TPayload) => Promise<TResult>,
  { throttleMs = 300 }: { throttleMs?: number } = {}
) {
  const cache = new Map<string, TResult>();
  const inFlight = new Map<string, Promise<TResult>>();

  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastPayload: TPayload | null = null;
  let lastKey: string | null = null;

  let waiters: {
    resolve: (v: TResult) => void;
    reject: (e: unknown) => void;
  }[] = [];

  const flush = async () => {
    timer = null;

    const payload = lastPayload!;
    const key = lastKey!;

    try {
      if (cache.has(key)) {
        const result = cache.get(key)!;
        waiters.forEach((w) => w.resolve(result));
        waiters = [];
        return;
      }

      let promise = inFlight.get(key);

      if (!promise) {
        promise = asyncFn(payload).then((result) => {
          cache.set(key, result);
          inFlight.delete(key);
          return result;
        });

        inFlight.set(key, promise);
      }

      const result = await promise;
      waiters.forEach((w) => w.resolve(result));
    } catch (err) {
      waiters.forEach((w) => w.reject(err));
    } finally {
      waiters = [];
    }
  };

  return (payload: TPayload): Promise<TResult> => {
    const key = JSON.stringify(payload);

    if (cache.has(key)) {
      return Promise.resolve(cache.get(key)!);
    }

    if (inFlight.has(key)) {
      return inFlight.get(key)!;
    }

    lastPayload = payload;
    lastKey = key;

    if (timer) clearTimeout(timer);
    timer = setTimeout(flush, throttleMs);

    return new Promise<TResult>((resolve, reject) => {
      waiters.push({ resolve, reject });
    });
  };
}
